import { prefsApi } from '@/shared/api/services/prefsApi';
import { adaptPreferencias } from '@/shared/domain/adapters';
import {
  mismaConfig,
  resolverConflicto,
  type OpcionConflicto,
} from '@/shared/domain/prefsConflicto';
import type { PreferenciasConfig } from '@/shared/domain/types';
import {
  markSyncPending,
  replaceConfig,
  syncConflict,
  syncFailed,
  syncStarted,
  syncSucceeded,
} from '../slices/prefsSlice';
import type { AppStoreLike } from './types';

/** Espera antes de subir los cambios (agrupa ediciones seguidas). */
export const DEBOUNCE_PREFS_MS = 1500;

export interface PrefsSyncOptions {
  debounceMs?: number;
}

const usuarioActual = (store: AppStoreLike): string | null => {
  const { auth } = store.getState();
  return auth.status === 'auth' ? (auth.user?.id ?? null) : null;
};

/**
 * Sincroniza las preferencias con la cuenta (docs/ARQUITECTURA.md §5):
 *  - al iniciar sesión: sin preferencias en el servidor se suben las locales; con preferencias y locales
 *    personalizadas distintas se abre un conflicto para que el usuario elija; si no, se adoptan las del servidor,
 *  - cambios locales → espera 1,5 s → PUT con `version_base`; 409 `version_desfasada` → conflicto,
 *  - sin red queda pendiente y se reintenta al volver la conexión o al editar de nuevo.
 */
export function startPrefsSync(store: AppStoreLike, options: PrefsSyncOptions = {}): () => void {
  const debounceMs = options.debounceMs ?? DEBOUNCE_PREFS_MS;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let programadoPara: string | null = null;
  let ocupado = false;
  /** Tras iniciar sesión (o arrancar con ella) hay que comparar con el servidor antes de subir nada. */
  let reconciliarAntes = false;
  let usuario: string | null = null;
  let sinRed = store.getState().ui.offline;
  let alcanzable = store.getState().ui.apiReachable;

  const limpiar = (): void => {
    if (timer) clearTimeout(timer);
    timer = null;
    programadoPara = null;
  };

  async function subir(versionBase: number): Promise<void> {
    const { config, updatedAt } = store.getState().prefs;
    store.dispatch(syncStarted());
    const r = await prefsApi.putPreferencias(versionBase, config);
    if (usuarioActual(store) === null) return;
    if (r.ok) {
      store.dispatch(syncSucceeded({ version: r.data.version }));
      if (store.getState().prefs.updatedAt !== updatedAt) store.dispatch(markSyncPending());
    } else if (r.status === 409 && r.code === 'version_desfasada') {
      const remota = adaptPreferencias(r.extra);
      if (remota.config)
        store.dispatch(
          syncConflict({
            version: remota.version,
            config: remota.config,
            actualizado: remota.actualizado ?? '',
          }),
        );
      else store.dispatch(syncFailed(r.detail));
    } else store.dispatch(syncFailed(r.status === 0 ? 'network' : r.detail));
  }

  async function reconciliar(): Promise<void> {
    const r = await prefsApi.getPreferencias();
    if (!r.ok) {
      store.dispatch(syncFailed(r.status === 0 ? 'network' : r.detail));
      return;
    }
    const { config: remota, version } = r.data;
    const local = store.getState().prefs;
    if (local.sync === 'conflict') return; // ya hay una decisión pendiente del usuario
    if (remota === null) await subir(0);
    else if (local.serverVersion === version) {
      // Misma base que la del servidor: solo falta subir lo que quedó pendiente (o a medias) la vez anterior.
      if (local.sync === 'pending' || local.sync === 'syncing' || local.sync === 'error')
        await subir(version);
      else store.dispatch(syncSucceeded({ version }));
    } else if (!local.customized || mismaConfig(local.config, remota)) {
      if (!mismaConfig(local.config, remota))
        store.dispatch(replaceConfig({ config: remota, customized: false }));
      store.dispatch(syncSucceeded({ version }));
    } else
      store.dispatch(
        syncConflict({ version, config: remota, actualizado: r.data.actualizado ?? '' }),
      );
  }

  async function ejecutar(): Promise<void> {
    if (ocupado || usuarioActual(store) === null) return;
    ocupado = true;
    try {
      const version = store.getState().prefs.serverVersion;
      if (version === null || reconciliarAntes) {
        reconciliarAntes = false;
        await reconciliar();
      } else await subir(version);
    } finally {
      ocupado = false;
    }
  }

  const programar = (updatedAt: string | null): void => {
    if (timer && programadoPara === updatedAt) return;
    limpiar();
    programadoPara = updatedAt;
    timer = setTimeout(() => {
      timer = null;
      void ejecutar();
    }, debounceMs);
  };

  const alCambiar = (): void => {
    const s = store.getState();
    const uid = usuarioActual(store);
    const volvioLaRed = (sinRed && !s.ui.offline) || (!alcanzable && s.ui.apiReachable);
    sinRed = s.ui.offline;
    alcanzable = s.ui.apiReachable;
    if (uid !== usuario) {
      usuario = uid;
      limpiar();
      reconciliarAntes = uid !== null;
      if (uid) void ejecutar();
      return;
    }
    if (!uid) return;
    if (volvioLaRed && s.prefs.sync === 'error' && s.prefs.syncError === 'network') {
      limpiar();
      void ejecutar();
    } else if (s.prefs.sync === 'pending' && s.prefs.serverVersion !== null)
      programar(s.prefs.updatedAt);
    else if (s.prefs.sync !== 'pending') limpiar();
  };

  const detener = store.subscribe(alCambiar);
  alCambiar();
  return () => {
    detener();
    limpiar();
  };
}

/**
 * Aplica la elección del usuario ante un conflicto de preferencias (diálogo "Usar la de la cuenta / Usar este
 * dispositivo / Mezclar favoritos"). Sin conflicto pendiente no hace nada.
 */
export function resolverConflictoPrefs(store: AppStoreLike, opcion: OpcionConflicto): void {
  const { conflict, config } = store.getState().prefs;
  if (!conflict) return;
  const resultado: PreferenciasConfig = resolverConflicto(config, conflict, opcion);
  if (resultado !== config)
    store.dispatch(replaceConfig({ config: resultado, customized: opcion !== 'servidor' }));
  store.dispatch(syncSucceeded({ version: conflict.version }));
  if (opcion !== 'servidor') store.dispatch(markSyncPending());
}
